import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { loginByToken, fetchUserProfile } from '../store/userSlice';
import LoginModal from './LoginModal';
import ErrorBoundary from './ErrorBoundary';
import { 
  Bot, 
  Activity, 
  Shield, 
  Settings,
  CheckCircle,
  Clock,
  AlertTriangle,
  Zap,
  Calendar,
  User,
  Crown
} from 'lucide-react';
import AIFleetGrid from './AI/AIFleetGrid';
import TopAnnouncementBar from './TopAnnouncementBar';

const AIFleetPage = () => {
  const { t, i18n } = useTranslation();
  const { fleetType } = useParams(); // 获取路由参数
  const [activeTab, setActiveTab] = useState(fleetType || 'gpt-backup'); // 使用路由参数或默认为'gpt-backup'（GPT Plus车队）
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoggedIn, loading, user } = useSelector(state => state.user);
  const [tokenLoginAttempted, setTokenLoginAttempted] = useState(false);
  const [defaultFleetId, setDefaultFleetId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false); // 控制登录弹窗
  const [forceUpdate, setForceUpdate] = useState(0); // 强制更新状态

  // 监听语言变化，强制重新渲染
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };

    // 监听i18n语言变化事件
    i18n.on('languageChanged', handleLanguageChange);
    
    // 监听自定义语言变化事件
    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // 根据路由参数设置默认选中的车队类型
  useEffect(() => {
    if (fleetType) {
      // 将路由参数转换为对应的fleet ID
      const fleetTypeToId = {
        'sora': 'fleet-002',
        'claude': 'fleet-003',
        'grok': 'fleet-004',
        'gpt-backup': 'fleet-005'
      };
      
      const selectedFleetId = fleetTypeToId[fleetType];
      if (selectedFleetId) {
        setDefaultFleetId(selectedFleetId);
        console.log('从路由选中车队:', fleetType, selectedFleetId);
      }
    }
  }, [fleetType]);

  // 当路由参数变化时更新activeTab
  useEffect(() => {
    console.log('路由参数变化检查:', { fleetType, pathname: window.location.pathname });
    if (fleetType) {
      setActiveTab(fleetType);
      console.log('设置activeTab为:', fleetType);
    } else {
      // 没有指定车队类型时，默认显示gpt-backup（主要GPT车队），但不重定向
      console.log('没有车队类型，使用默认显示');
      setActiveTab('gpt-backup');
    }
  }, [fleetType]);

  // 检查 URL 参数中的 userToken - 保留自动登录功能
  useEffect(() => {
    const userToken = searchParams.get('userToken');
    
    // 如果有 userToken 且还没尝试过 token 登录
    if (userToken && !tokenLoginAttempted) {
      setTokenLoginAttempted(true);
      console.log('尝试使用 userToken 自动登录:', userToken);
      
      // 设置超时处理，防止一直loading
      const timeoutId = setTimeout(() => {
        console.warn('Token登录超时，继续显示页面');
        // 清除URL参数，避免无限loading
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }, 10000); // 10秒超时
      
      // 使用 userToken 进行登录
      dispatch(loginByToken(userToken))
        .then((result) => {
          clearTimeout(timeoutId);
          if (loginByToken.fulfilled.match(result)) {
            console.log('Token 登录成功');
            // 登录成功后清除URL参数，强制更新状态
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            setForceUpdate(prev => prev + 1);
          } else {
            console.error('Token 登录失败:', result.payload);
            // 登录失败也清除URL参数，避免无限loading
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          console.error('Token 登录出错:', error);
          // 出错也清除URL参数，避免无限loading
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        });
    }
  }, [searchParams, tokenLoginAttempted, dispatch, navigate, fleetType]);

  // 简化的车队分类标签 - 只保留基本信息传递给Grid组件
  const fleetTabs = [
    { 
      id: 'all', 
      name: t('sidebar.userServices.aiShop'), 
      icon: Bot, 
      description: t('aiFleet.pageSubtitle')
    }
  ];

  // 处理需要登录的操作
  const handleLoginRequired = () => {
    setShowLoginModal(true);
  };

  // 处理登录成功
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // 强制更新组件状态以反映登录成功，不刷新页面
    setForceUpdate(prev => prev + 1);
  };

  // 处理关闭登录弹窗
  const handleCloseModal = () => {
    setShowLoginModal(false);
  };


  // 如果正在进行 token 自动登录，显示加载状态
  // 修改加载条件：只在有userToken且正在尝试登录时显示loading
  if (loading && searchParams.get('userToken') && !tokenLoginAttempted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('aiFleet.verifyingLogin')}</p>
          <p className="text-xs text-gray-400 mt-2">如果长时间无响应，请刷新页面</p>
        </div>
      </div>
    );
  }

  // 直接渲染页面内容，不检查登录状态
  return (
    <ErrorBoundary 
      title="AI车队页面出错"
      message="页面加载失败，请尝试刷新页面。"
      onReset={() => {
        setForceUpdate(prev => prev + 1);
        window.location.reload();
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        
        {/* 顶部公告栏 */}
        <TopAnnouncementBar />
        
        {/* 主要布局：简化为单栏布局 */}
        <div className="relative">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6">
            {/* AI车队内容 */}
            <ErrorBoundary 
              title="AI车队加载出错"
              message="车队信息加载失败，请尝试刷新页面。"
              onReset={() => setForceUpdate(prev => prev + 1)}
            >
              <AIFleetGrid 
                activeTab={activeTab} 
                tabs={fleetTabs}
                defaultSelectedFleetId={defaultFleetId}
                user={user}
                navigate={navigate}
                isLoggedIn={isLoggedIn}
                onLoginRequired={handleLoginRequired}
              />
            </ErrorBoundary>
          </div>
        </div>

        {/* 登录弹窗 - 在需要时显示 */}
        <LoginModal 
          isOpen={showLoginModal}
          onClose={handleCloseModal}
          onLogin={handleLoginSuccess}
        />
      </div>
    </ErrorBoundary>
  );
};

export default AIFleetPage; 